import { useCallback, useEffect, useState } from 'react';
import { Copy, Download, Eye, Link2, Lock, Share2, Star, Trash2, X } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import Ayuda from '../ui/Ayuda';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import { CLASIFICACION, ESTADO_ANTIVIRUS, cuando, tamano } from './formato';

const ACCION = {
  SUBIO: 'subió', NUEVA_VERSION: 'subió una versión nueva', ABRIO: 'lo abrió', DESCARGO: 'lo descargó',
  RENOMBRADO: 'lo renombró', MOVIDO: 'lo movió', COMPARTIO: 'lo compartió', DEJO_DE_COMPARTIR: 'dejó de compartirlo',
  ENLACE_CREADO: 'creó un enlace', ENLACE_REVOCADO: 'revocó un enlace', A_PAPELERA: 'lo mandó a la papelera',
  RESTAURADO: 'lo restauró', VERSION_RESTAURADA: 'restauró una versión', CLASIFICADO: 'lo clasificó',
  BORRADO_DEFINITIVO: 'lo borró de verdad', SUBIO_IGUAL: 'lo volvió a subir igual',
};

function descargar(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nombre; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** El cajón lateral de un archivo. */
export default function Ficha({ id, onCerrar, onCambio }) {
  const { usuario } = useAuth();
  const [f, setF] = useState(null);
  const [error, setError] = useState(null);
  // Aviso, fallo y visor van atados al id: al cambiar de archivo se apagan
  // solos sin tocar estado dentro de un efecto.
  const [avisoDe, setAvisoDe] = useState(null);
  const [falloDe, setFalloDe] = useState(null);
  const [visorDe, setVisorDe] = useState(null);
  const aviso = avisoDe?.id === id ? avisoDe.texto : null;
  const fallo = falloDe?.id === id ? falloDe.texto : null;
  const visor = visorDe === id;
  const setAviso = (t) => setAvisoDe(t ? { id, texto: t } : null);
  const setFallo = (t) => setFalloDe(t ? { id, texto: t } : null);
  const setVisor = (v) => setVisorDe(typeof v === 'function' ? (v(visor) ? id : null) : (v ? id : null));
  const [motivo, setMotivo] = useState('');
  const [compartirCon, setCompartirCon] = useState({ tipo: 'rol', rol: 'ENCARGADO', usuario: '', nivel: 'VER' });
  const [enlace, setEnlace] = useState({ dias: 7, contrasena: '', tope: '' });
  const [personas, setPersonas] = useState([]);

  // La ficha se lee al cambiar de archivo y cada vez que `cargar()` lo pide
  // (un contador: así el efecto solo suscribe y el estado se toca en la respuesta).
  const [tick, setTick] = useState(0);
  const cargar = useCallback(async () => { setTick((t) => t + 1); }, []);
  useEffect(() => {
    let vivo = true;
    api.get(`/archivos/${id}`).then((r) => { if (vivo) { setF(r); setError(null); } }, (e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [id, tick]);
  const puedeCompartir = Boolean(f?.puede_compartir);
  useEffect(() => {
    if (!puedeCompartir) return undefined;
    let vivo = true;
    api.get('/usuarios/directorio').then((r) => { if (vivo) setPersonas(Array.isArray(r) ? r : []); }, () => { if (vivo) setPersonas([]); });
    return () => { vivo = false; };
  }, [puedeCompartir]);

  async function accion(fn, exito) {
    setFallo(null); setAviso(null);
    try {
      const r = await fn();
      setAviso(exito || r?.dice || 'Hecho.');
      await cargar();
      onCambio?.();
      return r;
    } catch (e) {
      setFallo(e instanceof ErrorApi ? e.message : 'No se pudo hacer. Revisa la conexión y vuelve a intentarlo.');
      return null;
    }
  }

  if (error) return <ErrorConReintento que="No se pudo abrir la ficha" porque={error} onReintentar={cargar} />;
  if (!f) return <Cargando filas={4} etiqueta="Abriendo la ficha" />;
  const clasif = CLASIFICACION[f.clasificacion] || CLASIFICACION.INTERNO;

  return (
    <aside className="bg-white border border-gray-200 rounded-2xl p-4 space-y-4 min-w-0" aria-label={`Ficha de ${f.nombre}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 break-words">{f.nombre}</div>
          <div className="text-xs text-gray-500">{tamano(f.bytes)} · versión {f.version} · {cuando(f.actualizado_en)}</div>
          <div className="flex flex-wrap gap-1 mt-1">
            <span className={`text-[11px] px-2 py-0.5 rounded ${clasif.tono}`}>{f.clasificacion !== 'INTERNO' && <Lock className="inline h-3 w-3 mr-0.5" />}{clasif.texto}</span>
            <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-700">{ESTADO_ANTIVIRUS[f.revision] || f.revision}</span>
            {f.solo_lectura && <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-700">Solo lectura</span>}
          </div>
        </div>
        <button type="button" onClick={onCerrar} className="text-gray-500" aria-label="Cerrar la ficha"><X className="h-5 w-5" /></button>
      </div>

      {aviso && <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded p-2">{aviso}</div>}
      {fallo && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{fallo}</div>}

      <div className="flex flex-wrap gap-2">
        {f.previsualizable && (
          <button type="button" onClick={() => setVisor((v) => !v)} className="border border-gray-300 px-3 py-2 rounded-lg text-sm flex items-center gap-1">
            <Eye className="h-4 w-4" /> {visor ? 'Cerrar vista previa' : 'Ver aquí'}
          </button>
        )}
        <button type="button" className="bg-[var(--color-marca)] text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 font-semibold"
                onClick={() => accion(async () => {
                  const q = f.exige_motivo ? `?motivo=${encodeURIComponent(motivo)}` : '';
                  descargar(await api.bajar(`/archivos/${id}/descargar${q}`), f.nombre);
                  return { dice: 'Descargado.' };
                })}>
          <Download className="h-4 w-4" /> Descargar
        </button>
        <button type="button" className="border border-gray-300 px-3 py-2 rounded-lg text-sm flex items-center gap-1"
                onClick={() => accion(() => api.post(`/archivos/${id}/favorito?marcar=${!f.favorito}`, {}), f.favorito ? 'Quitado de favoritos.' : 'Añadido a favoritos.')}>
          <Star className={`h-4 w-4 ${f.favorito ? 'fill-amber-400 text-amber-500' : ''}`} /> {f.favorito ? 'Favorito' : 'Favorito'}
        </button>
        <button type="button" className="border border-gray-300 px-3 py-2 rounded-lg text-sm flex items-center gap-1"
                onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/#archivo=${id}`); setAviso('Enlace interno copiado: quien lo abra tiene que iniciar sesión.'); }}>
          <Copy className="h-4 w-4" /> Copiar enlace interno
        </button>
      </div>
      {f.exige_motivo && (
        <label className="block text-xs text-gray-700">
          Motivo de la descarga (tu empresa lo pide para lo {clasif.texto.toLowerCase()})
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Por ejemplo: auditoría del cliente" />
        </label>
      )}
      {visor && f.previsualizable && (
        <iframe title={`Vista previa de ${f.nombre}`} src={`/api/archivos/${id}/visor`} sandbox="" className="w-full h-[60vh] border border-gray-200 rounded-xl bg-gray-50" />
      )}

      {/* Versiones */}
      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">Versiones <Ayuda id="archivos.ficha.versiones" /></h3>
        <ul className="mt-1 space-y-1">
          {(f.versiones || []).map((v) => (
            <li key={v.numero} className="flex items-center gap-2 text-sm">
              <span className={v.vigente ? 'font-semibold text-gray-900' : 'text-gray-700'}>v{v.numero}</span>
              <span className="text-xs text-gray-500 flex-1 min-w-0 truncate">{tamano(v.bytes)} · {v.por || 'alguien'} · {cuando(v.creada_en)}</span>
              {v.vigente ? <span className="text-xs text-green-700">Vigente</span>
                : v.disponible && f.puede_editar
                  ? <button type="button" className="text-xs text-[var(--color-marca)] font-semibold" onClick={() => accion(() => api.post(`/archivos/${id}/versiones/${v.numero}/restaurar`, {}), `Versión ${v.numero} restaurada como nueva versión vigente.`)}>Restaurar</button>
                  : <span className="text-xs text-gray-400">{v.disponible ? '' : 'ya no se conserva'}</span>}
            </li>
          ))}
        </ul>
      </section>

      {/* Compartir por dentro */}
      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><Share2 className="h-3.5 w-3.5" /> Compartido con <Ayuda id="archivos.ficha.compartir" /></h3>
        {(f.comparticiones || []).length === 0 && <p className="text-xs text-gray-500 mt-1">Con nadie todavía.</p>}
        <ul className="mt-1 space-y-1">
          {(f.comparticiones || []).map((c) => (
            <li key={c.id} className="flex items-center gap-2 text-sm">
              <span className="flex-1 min-w-0 truncate text-gray-900">{c.persona || `Rol ${c.rol}`} <span className="text-xs text-gray-500">({c.nivel === 'VER' ? 'ver' : 'editar'})</span></span>
              {f.puede_compartir && <button type="button" className="text-xs text-red-700" onClick={() => accion(() => api.post(`/archivos/compartir/${c.id}/revocar`, {}))}>Quitar</button>}
            </li>
          ))}
        </ul>
        {f.puede_compartir && (
          <form className="mt-2 flex flex-wrap gap-2 items-end" onSubmit={(e) => {
            e.preventDefault();
            accion(() => api.post('/archivos/compartir', {
              id_archivo: id, nivel: compartirCon.nivel,
              con_rol: compartirCon.tipo === 'rol' ? compartirCon.rol : null,
              con_usuario: compartirCon.tipo === 'persona' && compartirCon.usuario ? Number(compartirCon.usuario) : null,
            }));
          }}>
            <label className="text-xs text-gray-700">Con
              <select value={compartirCon.tipo} onChange={(e) => setCompartirCon({ ...compartirCon, tipo: e.target.value })} className="block border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                <option value="rol">un rol</option><option value="persona">una persona</option>
              </select>
            </label>
            {compartirCon.tipo === 'rol' ? (
              <label className="text-xs text-gray-700">Rol
                <select value={compartirCon.rol} onChange={(e) => setCompartirCon({ ...compartirCon, rol: e.target.value })} className="block border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                  {['CONSULTA', 'OPERADOR', 'ENCARGADO', 'ADMIN'].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
            ) : (
              <label className="text-xs text-gray-700">Persona
                <select value={compartirCon.usuario} onChange={(e) => setCompartirCon({ ...compartirCon, usuario: e.target.value })} className="block border border-gray-300 rounded-lg px-2 py-1.5 text-sm max-w-[12rem]">
                  <option value="">Elige…</option>
                  {personas.filter((p) => p.id !== usuario?.id).map((p) => <option key={p.id} value={p.id}>{p.nombre_completo || p.nombre || p.email}</option>)}
                </select>
              </label>
            )}
            <label className="text-xs text-gray-700">Para
              <select value={compartirCon.nivel} onChange={(e) => setCompartirCon({ ...compartirCon, nivel: e.target.value })} className="block border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                <option value="VER">ver</option><option value="EDITAR">editar</option>
              </select>
            </label>
            <button type="submit" className="border border-gray-300 px-3 py-1.5 rounded-lg text-sm font-semibold">Compartir</button>
          </form>
        )}
      </section>

      {/* Enlaces externos */}
      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><Link2 className="h-3.5 w-3.5" /> Enlaces para gente de fuera <Ayuda id="archivos.ficha.enlace" /></h3>
        {f.clasificacion !== 'INTERNO' && <p className="text-xs text-gray-600 mt-1">Lo {clasif.texto.toLowerCase()} no se comparte por enlace: compártelo por dentro con quien lo necesite.</p>}
        <ul className="mt-1 space-y-1">
          {(f.enlaces || []).map((e) => (
            <li key={e.id} className="flex items-center gap-2 text-sm">
              <span className={`text-[11px] px-2 py-0.5 rounded ${e.estado === 'ACTIVO' ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-600'}`}>{e.estado.toLowerCase()}</span>
              <span className="flex-1 min-w-0 truncate text-xs text-gray-600">hasta {cuando(e.caduca_en)} · {e.descargas} descarga(s){e.con_contrasena ? ' · con contraseña' : ''}</span>
              {e.estado === 'ACTIVO' && f.puede_enlace && <button type="button" className="text-xs text-red-700" onClick={() => accion(() => api.post(`/archivos/enlaces/${e.id}/revocar`, {}))}>Revocar</button>}
            </li>
          ))}
        </ul>
        {f.puede_enlace && (
          <form className="mt-2 flex flex-wrap gap-2 items-end" onSubmit={(e) => {
            e.preventDefault();
            accion(async () => {
              const r = await api.post(`/archivos/${id}/enlaces`, { dias: Number(enlace.dias), contrasena: enlace.contrasena || null, tope_descargas: enlace.tope ? Number(enlace.tope) : null });
              const url = `${window.location.origin}${r.ruta}`;
              try { await navigator.clipboard?.writeText(url); } catch { /* sin portapapeles */ }
              return { dice: `${r.dice} Copiado: ${url}` };
            });
          }}>
            <label className="text-xs text-gray-700">Vale (días)
              <input type="number" min="1" max="30" value={enlace.dias} onChange={(e) => setEnlace({ ...enlace, dias: e.target.value })} className="block w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs text-gray-700">Contraseña (opcional)
              <input type="text" value={enlace.contrasena} onChange={(e) => setEnlace({ ...enlace, contrasena: e.target.value })} className="block w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" autoComplete="off" />
            </label>
            <label className="text-xs text-gray-700">Tope de descargas
              <input type="number" min="1" value={enlace.tope} onChange={(e) => setEnlace({ ...enlace, tope: e.target.value })} className="block w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" placeholder="sin tope" />
            </label>
            <button type="submit" className="border border-gray-300 px-3 py-1.5 rounded-lg text-sm font-semibold">Crear enlace</button>
          </form>
        )}
      </section>

      {/* Clasificación */}
      {f.puede_clasificar && (
        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase">Clasificación <Ayuda id="archivos.ficha.clasificacion" /></h3>
          <div className="flex flex-wrap gap-1 mt-1">
            {[['', 'Como su carpeta'], ['INTERNO', 'Interno'], ['CONFIDENCIAL', 'Confidencial'], ['DATOS_PERSONALES', 'Datos personales']].map(([v, t]) => (
              <button key={v} type="button" onClick={() => accion(() => api.post(`/archivos/${id}/clasificar`, { clasificacion: v || null }), 'Clasificación guardada.')}
                      className={`text-xs px-2 py-1 rounded-lg border ${(f.clasificacion_propia || '') === v ? 'border-[var(--color-marca)] text-[var(--color-marca)] font-semibold' : 'border-gray-300 text-gray-700'}`}>{t}</button>
            ))}
          </div>
        </section>
      )}

      {/* Actividad */}
      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase">Quién lo abrió y cuándo</h3>
        <ul className="mt-1 space-y-0.5 max-h-48 overflow-auto">
          {(f.actividad || []).map((x, i) => (
            <li key={i} className="text-xs text-gray-700">{x.quien || 'Alguien'} {ACCION[x.accion] || x.accion.toLowerCase()} · {cuando(x.en)}{x.motivo ? ` · «${x.motivo}»` : ''}</li>
          ))}
        </ul>
      </section>

      {f.estado === 'ACTIVO' && f.puede_editar && (
        <button type="button" className="text-sm text-red-700 flex items-center gap-1" onClick={() => accion(() => api.post(`/archivos/${id}/papelera`, {}))}>
          <Trash2 className="h-4 w-4" /> Mandar a la papelera
        </button>
      )}
    </aside>
  );
}
